import { ComponentType, SVGProps } from "react";
import { DocumentEntityType } from "../common/components/search/SearchResultList.types";
import { ContractFilterRequest } from "../contract/ContractFilter.types";
import { maxAiDocumentSizeInBytes } from "@common/components/dotLegalFileUpload/maxDocumentSize";

export interface AiChatScope {
    contracts?: Partial<ContractFilterRequest>;
}

export interface AiChatSuggestion {
    labelKey: string;
    promptKey: string;
    icon: ComponentType<SVGProps<SVGSVGElement>>;
    scope: AiChatScope;
}

export interface AiChatMessage {
    id?: string;
    role: "user" | "assistant";
    content: string;
    activity?: AiChatActivityEntry[];
    sources?: AiChatSourceReference[];
    entityReferences?: AiChatEntityReference[];
    resultsTruncated?: boolean;
    attachments?: AiChatAttachment[];
    usedDeepAnalysis?: boolean;
    feedbackIsPositive?: boolean | null;
}

export interface AiChatActivityEntry {
    kind: "thinking" | "tool_call";
    text: string;
    toolName?: string;
    attachmentId?: string;
}

export interface AiChatSourceReference {
    documentId: string;
    documentVersionId: string;
    documentTitle: string;
    entityTitle: string;
    entityType: DocumentEntityType;
    entityId: string;
    pageNumber?: number;
    chunkContent?: string;
    fileName?: string;
    documentDate?: string;
}

export interface AiChatEntityReference {
    entityId: string;
    entityName: string;
    entityType: string;
}

export interface AiChatStreamEvent {
    type: "sources" | "entity_references" | "results_truncated" | "tool_call" | "thinking" | "delta" | "done" | "error" | "deep_analysis";
    content?: string;
    toolName?: string;
    attachmentId?: string;
    sources?: AiChatSourceReference[];
    entityReferences?: AiChatEntityReference[];
    conversationId?: string;
    messageId?: string;
}

export interface AiChatConversationListItem {
    id: string;
    title: string;
    updatedAt: string;
    hasProcessingAttachments: boolean;
}

export interface AiChatConversationDetail {
    id: string;
    title: string;
    messages: AiChatConversationDetailMessage[];
}

export interface AiChatConversationDetailMessage {
    id: string;
    role: number;
    content: string;
    sources?: AiChatSourceReference[];
    attachments?: AiChatAttachment[];
    createdAt: string;
    feedbackIsPositive?: boolean | null;
    feedbackComment?: string | null;
}

export enum AiChatAttachmentStatus {
    Processing = 1,
    Ready = 2,
    Failed = 3,
}

export interface AiChatAttachment {
    id: string;
    fileName: string;
    fileSize: number;
    contentType: string;
    status: AiChatAttachmentStatus;
    failureReason?: string;
    uploadedAt: string;
}

export type AttachmentPreviewTarget = Pick<AiChatAttachment, "id" | "fileName">;

export const AiChatAttachmentLimits = {
    maxFileSizeBytes: maxAiDocumentSizeInBytes,
    maxAttachmentsPerConversation: 20,
    allowedExtensions: [
        ".pdf",
        ".docx",
        ".xlsx",
        ".xlsm",
        ".pptx",
        ".msg",
        ".eml",
        ".txt",
        ".csv",
        ".png",
        ".gif",
        ".jpeg",
        ".jpg",
        ".bmp",
        ".webp",
        ".mpo",
        ".heif",
        ".avif",
        ".tiff",
    ],
};
